import { allModulesLabel, isAllModulesProduct, moduleLabels, moduleOfProduct, type LineOfBusiness, type ModuleCode } from '@/lib/modules'

/** One environment of a company with the rights it holds, as `/companies/{id}/licenses` answers it. */
export type LicenseEnvironment = {
  environmentId: string
  name: string
  hosting: string
  subdomain?: string | null
  provisioningStatus: string
  grants: Array<{ application: string; productCode?: string; quantity: number; paidUntil: string; accessUntil: string; source: string; period?: string }>
  lineOfBusiness?: LineOfBusiness | null
  modules?: Array<{ code: ModuleCode; productCode: string; paidUntil?: string; cancelAt?: string; active: boolean }>
  /** Of the branches, `courtesyBranches` are given at no cost; the first of them serves until `courtesyLastDay`. */
  limits?: { branches: number; users: number; additionalUsers: number; courtesyBranches?: number; courtesyLastDay?: string | null } | null
  /** A suspended cloud environment: the day its data is deleted unless a plan is bought before. */
  deletesOn?: string | null
  /** A cloud environment whose data was deleted after 90 days suspended. */
  deletedAt?: string | null
}

/** A company's subscriptions and latest orders, as `/companies/{id}/billing` answers them. */
export type Billing = {
  subscriptions: Array<{ id: string; environmentId: string; environmentName: string; application: string; productCode?: string; period: string; renewalMode: string; quantity: number; unitPriceCents: number; paidUntil: string; graceUntil?: string; cancelAtPeriodEnd: boolean; status: string }>
  orders: Array<{ id: string; kind: string; period: string; renewalMode: string; status: string; totalCents: number; currency: string; createdAt: string; paidAt?: string; periodEnd?: string }>
}

/**
 * A company's stamps, as `/timbres/companies/{id}` answers them: its stamp account at SW (none yet, being opened
 * or fed, or ready in its store), what SW last reported as available and used, read at `syncedAt` (absent while
 * SW has never answered), its RFC and the packages it bought, each with when its stamps reached the account.
 */
export type TimbreBalance = {
  available?: number | null
  used?: number | null
  account: 'NONE' | 'PENDING' | 'READY'
  syncedAt?: string | null
  taxId?: string | null
  packages: Array<{ productName: string; quantity: number; credits: number; paidAt?: string | null; deliveredAt?: string | null }>
}

/** The shape of an RFC, a company's (three letters) or a person's (four), as the server checks it. */
export const taxIdPattern = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/

export const licenseLabels: Record<string, string> = { CLOUD_BASE: 'BsPOS', CLOUD_DESKTOP_TERMINAL: 'BsPOS Terminal', CLOUD_MOBILE_TERMINAL: 'BsPOS móvil', CLOUD_USER: 'Usuario adicional' }

/** How a sold product reads: a module by its name, the bundle by its own, a licence by its label, anything else by the fallback. */
export function productLabel(productCode: string | undefined, fallback: string) {
  if (productCode && isAllModulesProduct(productCode)) return allModulesLabel
  const module = productCode ? moduleOfProduct(productCode) : undefined
  return module ? `Módulo ${moduleLabels[module]}` : licenseLabels[productCode ?? ''] ?? fallback
}

export function orderStatus(status: string): string {
  return {
    PENDING_PAYMENT: 'Esperando confirmación de Stripe',
    FULFILLMENT_PENDING: 'Pago confirmado; preparando el servicio',
    REVIEW_REQUIRED: 'La compra requiere revisión',
    FULFILLED: 'Licencias activas',
    CANCELED: 'Cancelado sin pago',
  }[status] ?? status
}
